import { type } from 'arktype';
import type { TranslatableText } from '@zag/core/common';
import { translatableTextSchema } from '@zag/core/models';
import { PlanVersionStatus } from './plan-version.model';

export const PLAN_TRANSLATABLE_FIELDS = ['name', 'description'] as const;
export type PlanTranslatableField = (typeof PLAN_TRANSLATABLE_FIELDS)[number];
export type PlanTranslations = TranslatableText<PlanTranslatableField>;

export const planQuerySchema = type({
  'strategyId?': 'string',
  'teamId?': 'string',
  'name?': 'string',
  'createdBy?': 'string',
});

export type PlanQuery = typeof planQuerySchema.t;

/**
 * Model for creating a new plan.
 * The plan will be created with an initial draft version.
 *
 * Note: createdBy is intentionally NOT included in this schema.
 * It should be derived from the authenticated user context on the server,
 * not supplied by the client (security concern).
 */
export const createPlanSchema = type({
  'name?': type('string>=1').configure({
    description: 'Display name of the plan shown throughout the UI.',
    examples: ['Week of 2026-04-20', 'April 2026 roster'],
  }),
  teamId: type('string>=1').configure({
    description: 'ID of the team this plan belongs to.',
    examples: ['team_abc123'],
  }),
  strategyId: type('string>=1').configure({
    description: 'ID of the planning strategy that provides shift patterns and working-day configuration.',
    examples: ['strategy_abc123'],
  }),
  startDate: type('string').configure({
    description: 'Start of the planning period as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-20'],
  }),
  endDate: type('string').configure({
    description: 'End of the planning period as an ISO 8601 calendar date (YYYY-MM-DD).',
    examples: ['2026-04-26'],
  }),
  'description?': type('string').configure({
    description: 'Optional free-form description of the plan.',
    examples: ['Reinforced evening coverage for the spring event.'],
  }),
  'shiftIds?': type('string[]').configure({
    description: 'Optional list of shift definition IDs to include in the plan. Stored in the initial version data.',
    examples: [['shift_abc123', 'shift_def456']],
  }),
  'translations?': translatableTextSchema(PLAN_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations for `name` and `description`. When provided, ' +
      '`translations.en.<field>` is mirrored into the legacy column for back-compat.',
  }),
}).configure({ description: 'Payload for creating a new plan together with its initial draft version.' });

export type CreatePlan = typeof createPlanSchema.t;

/**
 * Model for updating plan metadata.
 * All fields are optional, but at least one must be provided.
 * Does not update version data (that's handled by draft-state.service).
 */
export const updatePlanSchema = type({
  'name?': type('string>=1').configure({
    description: 'Updated display name of the plan.',
    examples: ['Week of 2026-04-20'],
  }),
  'description?': type('string').configure({
    description: 'Updated free-form description of the plan.',
    examples: ['Adjusted plan after sickness notifications.'],
  }),
  'startDate?': type('string').configure({
    description: 'Updated start of the planning period (ISO 8601 calendar date YYYY-MM-DD).',
    examples: ['2026-04-20'],
  }),
  'endDate?': type('string').configure({
    description: 'Updated end of the planning period (ISO 8601 calendar date YYYY-MM-DD).',
    examples: ['2026-04-26'],
  }),
  'translations?': translatableTextSchema(PLAN_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations to merge into the existing translations map. ' +
      'Languages and fields omitted are preserved. Setting a field to `null` clears it.',
  }),
}).configure({
  description: 'Partial update of plan metadata. Fields omitted from the body remain unchanged. Does not modify version data.',
});

export type UpdatePlan = typeof updatePlanSchema.t;

/**
 * Plan response type returned by the API.
 */
export interface PlanResponse {
  id: string;
  teamId: string;
  strategyId: string;
  name: string;
  description: string | null;
  startDate: string;
  endDate: string;
  status: PlanVersionStatus;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Expanded team object (from API with expand) */
  team?: { id: string; name: string } | null;
  /** Expanded strategy object (from API with expand) */
  strategy?: { id: string; name: string } | null;
  /** Full per-language translations map. Only populated when the caller asked for it via `?include=translations`. */
  translations?: TranslatableText<string>;
}

/**
 * Response model for plan creation.
 * Includes the created plan and its initial draft version.
 */
export interface CreatePlanResponse {
  /** The created plan */
  plan: {
    id: string;
    name: string;
    teamId: string;
    strategyId: string;
    startDate: string;
    endDate: string;
    description: string | null;
    createdBy: string | null;
    createdAt: Date;
    updatedAt: Date;
  };
  /** The initial draft version ID for immediate editing */
  draftVersionId: string;
}

export const createPlanResponseSchema = type({
  plan: type({
    id: 'string',
    name: 'string',
    teamId: 'string',
    strategyId: 'string',
    startDate: 'string',
    endDate: 'string',
    description: 'string | null',
    createdBy: 'string | null',
    createdAt: 'string',
    updatedAt: 'string',
  }),
  draftVersionId: 'string',
}).configure({ description: 'Created plan together with its initial draft version.' });
